import type { RestClient } from "./restClient";

/** One durable thing Artie has recorded about the user.
 *
 *  `id` is a real database key, not a position or a text hash — deletes and
 *  edits address it exactly, so a fact being reworded between render and
 *  action cannot make the control hit the wrong row.
 */
export interface UserFact {
  id: number;
  category: string;
  text: string;
  /** Pinned facts are exempt from extraction eviction and budget trimming. */
  pinned: boolean;
}

/** `not_configured` means no facts store is wired in this deployment, which
 *  is not the same as the user having no facts — the dialog must not imply
 *  Artie remembers nothing when it simply cannot look. */
export interface FactsResponse {
  status: "ok" | "not_configured";
  facts: UserFact[];
}

/** Matches the server-side cap; enforced here too so the user is told before
 *  a round trip rather than after a 400. */
export const MAX_FACT_CHARS = 500;

const FETCH_TIMEOUT_MS = 12_000;

export class FactsApi {
  constructor(private client: RestClient) {}

  /** Facts currently reaching the prompt. */
  list(signal?: AbortSignal): Promise<FactsResponse> {
    return this.client.json<FactsResponse>("/v1/memory/facts", {
      method: "GET",
      signal: signal ?? AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
  }

  /** Facts the user removed. Backs the Removed section, so removals are undoable. */
  listForgotten(signal?: AbortSignal): Promise<FactsResponse> {
    return this.client.json<FactsResponse>("/v1/memory/facts/forgotten", {
      method: "GET",
      signal: signal ?? AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
  }

  /** Assert a fact the extractor never inferred. */
  create(text: string, category = "preference"): Promise<{ status: string; fact: UserFact }> {
    return this.client.post("/v1/memory/facts", { text, category });
  }

  /** Soft-delete — reversible via restore(). */
  forget(id: number): Promise<{ status: string }> {
    return this.client.post(`/v1/memory/facts/${id}/forget`);
  }

  restore(id: number): Promise<{ status: string }> {
    return this.client.post(`/v1/memory/facts/${id}/restore`);
  }

  setPinned(id: number, pinned: boolean): Promise<{ status: string; pinned: boolean }> {
    return this.client.post(`/v1/memory/facts/${id}/pin`, { pinned });
  }

  /** Replace the text. The server supersedes rather than overwrites, so the
   *  original inference is kept alongside the correction. */
  edit(id: number, text: string): Promise<{ status: string; fact: UserFact }> {
    return this.client.post(`/v1/memory/facts/${id}/edit`, { text });
  }
}
